import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  Divider,
  Snackbar,
  Alert,
  Box,
  LinearProgress,
} from "@mui/material";
import { AppButton } from "../../../shared/components/AppButton";
import { Visibility, Delete, Receipt, Download } from "@mui/icons-material";
import { AppTable } from "../../../shared/components/AppTable";
import { AppTablePagination } from "../../../shared/components/AppTablePagination";
import { AppSearch } from "../../../shared/components/AppSearch";
import { Loader } from "../../../shared/components/Loader";
import { invoiceService } from "../../../shared/services/invoiceService";
import { clientService } from "../../../shared/services/clientService";
import { Invoice } from "../../../shared/types/invoice";
import { Client } from "../../../shared/types/client";
import { usePermission } from "../../../shared/contexts/PermissionContext";
import { InvoiceFilters, InvoiceFilterValues } from "./InvoiceFilters";

const fmtDate = (d?: string) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

export const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, hasPermission } = usePermission();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info" as "info" | "success" | "error",
  });
  const [deleteAnchorEl, setDeleteAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );
  const [appliedFilters, setAppliedFilters] = useState<InvoiceFilterValues>({
    invoiceNumber: "",
    client: "",
    advance: "",
    pending: "",
    partialPaid: "",
    total: "",
    dateFrom: "",
    dateTo: "",
  });

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await invoiceService.list({
        page: currentPage + 1,
        limit: pageSize,
      });
      const outerData = res?.data;
      const items: Invoice[] =
        outerData?.data ||
        outerData?.items ||
        (outerData as any)?.invoices ||
        [];
      setInvoices(
        (Array.isArray(items) ? items : []).map((inv: any) => ({
          ...inv,
          _id: inv._id ?? inv.id,
          clientCompanyName:
            typeof inv.clientId === "object"
              ? ((inv.clientId as any)?.displayName || (inv.clientId as any)?.companyName || (inv.clientId as any)?.ownerName)
              : inv.clientCompanyName,
        })),
      );
    } catch {
      setSnackbar({
        open: true,
        message: "Error loading invoices",
        severity: "error",
      });
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await clientService.list({ limit: 500 });
      const outerData = res?.data;
      const items =
        outerData?.data ||
        outerData?.items ||
        (outerData as any)?.clients ||
        [];
      setClients(
        (Array.isArray(items) ? items : []).map((c: any) => ({
          ...c,
          _id: c._id ?? c.id,
        })),
      );
    } catch {
      setClients([]);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Client-side filtering
  const filteredInvoices = React.useMemo(() => {
    let list = [...invoices];

    if (searchInput.trim()) {
      const q = searchInput.trim().toLowerCase();
      list = list.filter((inv) => {
        const clientName =
          typeof inv.clientId === "object"
            ? ((inv.clientId as any)?.displayName || (inv.clientId as any)?.companyName || (inv.clientId as any)?.ownerName)
            : inv.clientCompanyName;
        return (
          String(inv.invoiceNumber || "")
            .toLowerCase()
            .includes(q) ||
          String(clientName || "")
            .toLowerCase()
            .includes(q) ||
          String(inv.totalAmount ?? "")
            .toLowerCase()
            .includes(q) ||
          String(inv.advancePaidAmount ?? "")
            .toLowerCase()
            .includes(q)
        );
      });
    }

    if (appliedFilters.invoiceNumber) {
      list = list.filter(
        (inv) => String(inv.invoiceNumber) === appliedFilters.invoiceNumber,
      );
    }
    if (appliedFilters.client) {
      list = list.filter((inv) => {
        const name =
          typeof inv.clientId === "object"
            ? ((inv.clientId as any)?.displayName || (inv.clientId as any)?.companyName || (inv.clientId as any)?.ownerName)
            : inv.clientCompanyName;
        return name === appliedFilters.client;
      });
    }
    if (appliedFilters.advance) {
      const val = parseFloat(appliedFilters.advance);
      if (!isNaN(val))
        list = list.filter((inv) => inv.advancePaidAmount === val);
    }
    if (appliedFilters.pending) {
      const val = parseFloat(appliedFilters.pending);
      if (!isNaN(val))
        list = list.filter((inv) => {
          const pending =
            inv.totalPendingAmount ?? (inv.totalAmount - (inv.advancePaidAmount || 0) - (inv.partialPaidAmount || 0));
          return pending === val;
        });
    }
    if (appliedFilters.partialPaid) {
      const val = parseFloat(appliedFilters.partialPaid);
      if (!isNaN(val))
        list = list.filter((inv) => inv.partialPaidAmount === val);
    }
    if (appliedFilters.total) {
      const val = parseFloat(appliedFilters.total);
      if (!isNaN(val)) list = list.filter((inv) => inv.totalAmount === val);
    }
    if (appliedFilters.dateFrom) {
      const from = new Date(appliedFilters.dateFrom).getTime();
      list = list.filter(
        (inv) => inv.invoiceDate && new Date(inv.invoiceDate).getTime() >= from,
      );
    }
    if (appliedFilters.dateTo) {
      const to = new Date(appliedFilters.dateTo).getTime();
      list = list.filter(
        (inv) => inv.invoiceDate && new Date(inv.invoiceDate).getTime() <= to,
      );
    }
    return list;
  }, [invoices, searchInput, appliedFilters]);

  const paginatedInvoices = React.useMemo(() => {
    const start = currentPage * pageSize;
    return filteredInvoices.slice(start, start + pageSize);
  }, [filteredInvoices, currentPage, pageSize]);

  const totalPendingAmount = React.useMemo(
    () =>
      filteredInvoices.reduce((sum, inv) => {
        const pending =
          inv.totalPendingAmount ?? (inv.totalAmount - (inv.advancePaidAmount || 0) - (inv.partialPaidAmount || 0));
        return sum + (pending || 0);
      }, 0),
    [filteredInvoices],
  );

  const uniqueInvoiceNumbers = React.useMemo(
    () => [
      ...new Set(
        invoices.map((inv) => String(inv.invoiceNumber)).filter(Boolean),
      ),
    ],
    [invoices],
  );

  const uniqueClientNames = React.useMemo(
    () => [
      ...new Set(clients.map((c) => c.displayName || c.companyName || c.ownerName).filter(Boolean) as string[]),
    ],
    [clients],
  );

  const handleReset = () => {
    setAppliedFilters({
      invoiceNumber: "",
      client: "",
      advance: "",
      pending: "",
      partialPaid: "",
      total: "",
      dateFrom: "",
      dateTo: "",
    });
    setCurrentPage(0);
  };

  const handleDeleteClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    id: string,
  ) => {
    setDeleteAnchorEl(e.currentTarget);
    setSelectedInvoiceId(id);
  };

  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
    setSelectedInvoiceId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInvoiceId) return;
    try {
      await invoiceService.delete(selectedInvoiceId);
      setSnackbar({
        open: true,
        message: "Invoice deleted successfully",
        severity: "success",
      });
      fetchInvoices();
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Error deleting invoice';
      setSnackbar({
        open: true,
        message: msg,
        severity: "error",
      });
    } finally {
      handleDeleteClose();
    }
  };

  const handleExport = () => {
    const headers = [
      "Invoice Number",
      "Client Company",
      "Advance Paid",
      "Credited",
      "Total Pending",
      "Total Amount",
      "Invoice Date",
    ];
    const rows = filteredInvoices.map((inv) => {
      const clientName =
        typeof inv.clientId === "object"
          ? ((inv.clientId as any)?.displayName || (inv.clientId as any)?.companyName || (inv.clientId as any)?.ownerName)
          : inv.clientCompanyName;
      const pending =
        inv.totalPendingAmount ?? (inv.totalAmount - (inv.advancePaidAmount || 0) - (inv.partialPaidAmount || 0));
      return [
        inv.invoiceNumber,
        clientName || "",
        inv.advancePaidAmount,
        inv.partialPaidAmount || 0,
        pending,
        inv.totalAmount,
        fmtDate(inv.invoiceDate),
      ];
    });
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invoices.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col w-full min-h-full bg-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Receipt className="text-[#6366f1] w-8 h-8" />
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
            Invoices
          </h2>
        </div>
        <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
          <AppSearch
            value={searchInput}
            onChange={(v) => {
              setSearchInput(v);
              setCurrentPage(0);
            }}
            onSearch={() => setCurrentPage(0)}
            placeholder="Search"
            className="w-full sm:w-64"
          />
          {(isSuperAdmin() || hasPermission("invoices.add")) && (
            <AppButton
              variant="primary"
              onClick={() => navigate("/invoices/add")}
              sx={{ px: 3, py: 1.5, borderRadius: "6px" }}
            >
              Create Invoice
            </AppButton>
          )}
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-6 sm:px-10 py-2 border-b border-gray-100">
        <div className="flex gap-2">
          <Tooltip title="Export Invoice Report">
            <AppButton
              variant="primary"
              onClick={handleExport}
              aria-label="Export Invoice Report"
              sx={{
                borderRadius: "8px",
                backgroundColor: "#0EA5E9",
                "&:hover": { backgroundColor: "#0284C7" },
                px: 2,
                gap: 1,
              }}
            >
              <Download fontSize="small" />
              Export Invoice Report
            </AppButton>
          </Tooltip>
        </div>
        <div className="flex gap-2 flex-wrap">
          <AppButton
            variant="primary"
            disableRipple
            sx={{
              backgroundColor: "#22c55e",
              "&:hover": { backgroundColor: "#22c55e" },
              cursor: "default",
              px: 2,
              py: 1,
              borderRadius: "6px",
              minWidth: 240,
            }}
          >
            Total Pending Amount : {totalPendingAmount}
          </AppButton>
        </div>
      </div>

      {/* Table */}
      <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-x-auto">
        <InvoiceFilters
          invoiceNumbers={uniqueInvoiceNumbers}
          clientNames={uniqueClientNames}
          onApply={(filters) => {
            setAppliedFilters(filters);
            setCurrentPage(0);
          }}
          onReset={handleReset}
        />

        <div className="flex flex-col w-full flex-1">
          {isLoading && invoices.length > 0 && (
            <LinearProgress className="mb-4" />
          )}
          {isLoading && invoices.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              sx={{ backgroundColor: "#1f2937", borderRadius: "8px" }}
            >
              <Loader />
            </Box>
          ) : (
            <AppTable
              head={
                <>
                  <TableRow className="bg-gray-100">
                    <TableCell className="!font-semibold !text-gray-700">
                      Invoice Number
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Client Company Name
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Advance Paid
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Credited
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Total Pending
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Total Amount
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Invoice Date
                    </TableCell>
                    <TableCell className="!font-semibold !text-gray-700">
                      Actions
                    </TableCell>
                  </TableRow>
                </>
              }
              isEmpty={filteredInvoices.length === 0}
              emptyColSpan={8}
            >
              {paginatedInvoices.map((inv, index) => {
                const clientName =
                  typeof inv.clientId === "object"
                    ? ((inv.clientId as any)?.displayName || (inv.clientId as any)?.companyName || (inv.clientId as any)?.ownerName)
                    : inv.clientCompanyName;
                const pending =
                  inv.totalPendingAmount ??
                  inv.totalAmount - inv.advancePaidAmount;
                return (
                  <TableRow
                    key={inv._id}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <TableCell className="!text-gray-900 !font-medium">
                      {inv.invoiceNumber}
                    </TableCell>
                    <TableCell className="!text-gray-700">
                      {clientName || "—"}
                    </TableCell>
                    <TableCell className="!text-gray-700">
                      {inv.advancePaidAmount ?? 0}
                    </TableCell>
                    <TableCell className="!text-gray-700">
                      {inv.partialPaidAmount ?? 0}
                    </TableCell>
                    <TableCell className="!text-gray-700 font-semibold text-orange-600">
                      {pending ?? 0}
                    </TableCell>
                    <TableCell className="!text-gray-700">
                      {inv.totalAmount ?? 0}
                    </TableCell>
                    <TableCell className="!text-gray-700">
                      {fmtDate(inv.invoiceDate)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Tooltip title="View">
                          <IconButton
                            onClick={() =>
                              navigate(`/invoices/view/${inv._id}`, {
                                state: { backTo: "/invoices" },
                              })
                            }
                            size="small"
                            className="text-[#0EA5E9] hover:bg-blue-50"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(isSuperAdmin() ||
                          hasPermission("invoices.delete")) && (
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={(e) => handleDeleteClick(e, inv._id)}
                                size="small"
                                className="text-red-500 hover:bg-red-50"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </AppTable>
          )}
        </div>

        <AppTablePagination
          totalRows={filteredInvoices.length}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={(_, p) => setCurrentPage(p - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(0);
          }}
        />
      </div>

      {/* Delete Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              mt: 1.5,
              width: 260,
              boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
            },
          },
        }}
      >
        <div className="p-4 bg-white">
          <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
            Delete Invoice?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This invoice will be soft-deleted and hidden from the list.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <div className="flex justify-end gap-2">
            <AppButton
              variant="secondary"
              size="small"
              onClick={handleDeleteClose}
            >
              Cancel
            </AppButton>
            <AppButton
              variant="danger"
              size="small"
              onClick={handleDeleteConfirm}
            >
              Delete
            </AppButton>
          </div>
        </div>
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity as any} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
